import { type KeyboardEvent, useEffect, useMemo, useRef, useState } from 'react';
import { ActionIcon, Center, Group, ScrollArea, Text, Textarea, Tooltip } from '@mantine/core';
import { IconArrowUp } from '@tabler/icons-react';
import { formatChatMessageLinks, type ReceivedChatMessage } from '@livekit/components-react';
import { useTranslation } from 'react-i18next';

import { useReactionLog } from '../reactions';
import classes from './chat-panel.module.css';
import { participantName } from './participant-name';
import { SidePanel } from './side-panel';
import { notifyError } from '@/shared/notify';
import { UserAvatar } from '@/shared/user-avatar';

const SCROLL_BOTTOM_TOLERANCE = 48;

export function ChatPanel({
    messages,
    isSending,
    send,
    onClose,
}: Readonly<{
    messages: ReceivedChatMessage[];
    isSending: boolean;
    send: (message: string) => Promise<unknown>;
    onClose: () => void;
}>) {
    const { t, i18n } = useTranslation('conference');
    const { t: tReactions } = useTranslation('reactions');
    const [draft, setDraft] = useState('');
    const viewportRef = useRef<HTMLDivElement>(null);
    const atBottomRef = useRef(true);
    const reactionLog = useReactionLog();
    const feed = useMemo(
        () =>
            [
                ...messages.map((message) => ({ kind: 'message' as const, message, timestamp: message.timestamp })),
                ...reactionLog.map((reaction) => ({ kind: 'reaction' as const, reaction, timestamp: reaction.timestamp })),
            ].toSorted((a, b) => a.timestamp - b.timestamp),
        [messages, reactionLog]
    );
    const timeFormat = useMemo(
        () => new Intl.DateTimeFormat(i18n.language, { hour: '2-digit', minute: '2-digit' }),
        [i18n.language]
    );

    useEffect(() => {
        viewportRef.current?.scrollTo({ top: viewportRef.current.scrollHeight });
    }, [messages.length]);

    useEffect(() => {
        if (atBottomRef.current) viewportRef.current?.scrollTo({ top: viewportRef.current.scrollHeight });
    }, [reactionLog]);

    const submit = async () => {
        const text = draft.trim();
        if (!text || isSending) return;
        try {
            await send(text);
            setDraft('');
        } catch (error) {
            notifyError(t('chat.sendError'), error);
        }
    };

    const submitOnEnter = (event: KeyboardEvent<HTMLTextAreaElement>) => {
        if (event.key === 'Enter' && !event.shiftKey && !event.nativeEvent.isComposing) {
            event.preventDefault();
            void submit();
        }
    };

    return (
        <SidePanel closeLabel={t('chat.close')} title={t('chat.title')} onClose={onClose}>
            <ScrollArea
                className={classes.messages}
                viewportRef={viewportRef}
                onScrollPositionChange={({ y }) => {
                    const viewport = viewportRef.current;
                    if (viewport)
                        atBottomRef.current = viewport.scrollHeight - y - viewport.clientHeight < SCROLL_BOTTOM_TOLERANCE;
                }}
            >
                {feed.length === 0 ? (
                    <Center p="lg">
                        <Text c="dimmed" size="sm">
                            {t('chat.empty')}
                        </Text>
                    </Center>
                ) : (
                    feed.map((item) => {
                        if (item.kind === 'reaction') {
                            const { reaction } = item;
                            const author = participantName(reaction.from.name, reaction.from.identity);
                            return (
                                <div
                                    key={reaction.id}
                                    aria-label={tReactions('chat.aria', { name: author, emojis: reaction.emojis })}
                                    className={classes.reaction}
                                    role="note"
                                >
                                    <Text truncate className={classes.reactionText} size="sm">
                                        <Text inherit span c="dimmed">
                                            {author}:
                                        </Text>{' '}
                                        {reaction.emojis}
                                    </Text>
                                    <Text c="dimmed" size="xs">
                                        {tReactions('chat.label')} ·{' '}
                                        <time dateTime={new Date(reaction.timestamp).toISOString()}>
                                            {timeFormat.format(reaction.timestamp)}
                                        </time>
                                    </Text>
                                </div>
                            );
                        }
                        const { message } = item;
                        const author = participantName(message.from?.name, message.from?.identity);
                        return (
                            <div key={message.id} className={classes.message}>
                                <UserAvatar className={classes.messageAvatar} name={author} size="sm" />
                                <div className={classes.messageBody}>
                                    <Group gap="var(--spacing-2)" justify="space-between" wrap="nowrap">
                                        <Text truncate fw={500} size="sm">
                                            {author}
                                        </Text>
                                        <Text
                                            c="dimmed"
                                            component="time"
                                            dateTime={new Date(message.timestamp).toISOString()}
                                            size="xs"
                                        >
                                            {timeFormat.format(message.timestamp)}
                                        </Text>
                                    </Group>
                                    <Text className={classes.text} size="sm">
                                        {formatChatMessageLinks(message.message)}
                                    </Text>
                                </div>
                            </div>
                        );
                    })
                )}
            </ScrollArea>
            <form
                className={classes.composer}
                onSubmit={(event) => {
                    event.preventDefault();
                    void submit();
                }}
            >
                <Textarea
                    autosize
                    aria-label={t('chat.placeholder')}
                    classNames={{ input: classes.input }}
                    maxRows={6}
                    minRows={1}
                    placeholder={t('chat.placeholder')}
                    value={draft}
                    variant="unstyled"
                    onKeyDown={submitOnEnter}
                    onChange={(event) => {
                        setDraft(event.currentTarget.value);
                    }}
                />
                <div className={classes.sendRow}>
                    <Tooltip label={t('chat.send')}>
                        <ActionIcon
                            aria-label={t('chat.send')}
                            disabled={!draft.trim()}
                            loading={isSending}
                            radius="var(--radius-rounded-full)"
                            size="sm"
                            type="submit"
                            variant="filled"
                        >
                            <IconArrowUp size={16} />
                        </ActionIcon>
                    </Tooltip>
                </div>
            </form>
        </SidePanel>
    );
}
